'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { Slot } from '@radix-ui/react-slot';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import SvgDelete from '../../../__generated__/icon/SvgDelete';
const contentMotionClassName = cn('origin-[var(--radix-tooltip-content-transform-origin)] duration-150 motion-reduce:animate-none', 'animate-in fade-in-0 zoom-in-95', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95', 'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1');
interface TooltipProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  onContentClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  defaultOpen?: boolean;
  floatingPadding?: number;
  delayDuration?: number;
  asChild?: boolean;
  label: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  contentProps?: RadixTooltip.TooltipContentProps;
  isArrow?: boolean;
  /**
   * 툴팁이 표시될 방향을 지정합니다.
   * 'top': 트리거 요소 위에 툴팁 표시
   * 'bottom': 트리거 요소 아래에 툴팁 표시
   */
  side?: 'top' | 'bottom';
  /**
   * 툴팁 화살표를 기준으로 콘텐츠 박스의 정렬 방향을 지정합니다.
   * 'start': 왼쪽 정렬
   * 'center': 가운데 정렬 (기본값)
   * 'end': 오른쪽 정렬
   */
  align?: 'start' | 'center' | 'end';
  /**
   * 툴팁의 테마를 지정합니다.
   * 'dark': 어두운 테마 (기본값)
   * 'light': 밝은 테마
   * 'outlined': 흰색 배경 + 테두리 + 블러 스타일
   */
  theme?: 'dark' | 'light' | 'outlined';
  /**
   * 제목을 표시합니다.
   */
  title?: string;
  /**
   * X버튼을 표시할지 여부를 지정합니다.
   */
  showCloseButton?: boolean;
  /**
   * X버튼 클릭 시 호출되는 콜백 함수입니다.
   */
  onClose?: () => void;
}
export const Tooltip = ({
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  onContentClick,
  label,
  floatingPadding = 10,
  asChild,
  className,
  delayDuration = 200,
  triggerClassName,
  contentProps,
  isArrow = false,
  side,
  align,
  theme = 'dark',
  title,
  showCloseButton = false,
  onClose
}: TooltipProps) => {
  const [localOpened, setLocalOpened] = useState(defaultOpen);
  const isOpened = open ?? localOpened;
  const Button = asChild ? Slot : 'button';
  const toggle = () => {
    setLocalOpened(prev => !prev);
  };
  const handleClose = () => {
    setLocalOpened(false);
    onOpenChange?.(false);
    onClose?.();
  };

  // ESC 키로 닫기 (showCloseButton이 true일 때만)
  useEffect(() => {
    if (!showCloseButton || !isOpened) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCloseButton, isOpened]);
  const isLightTheme = theme === 'light';
  const isOutlined = theme === 'outlined';
  const isDialogMode = showCloseButton;
  return <RadixTooltip.Provider delayDuration={isDialogMode ? 0 : delayDuration}>
      <RadixTooltip.Root defaultOpen={defaultOpen} delayDuration={0} open={open ?? isOpened} onOpenChange={toggle}>
        <RadixTooltip.Trigger asChild onClick={toggle}>
          <Button className={triggerClassName}>{children}</Button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content className={cn('whitespace-pre-wrap break-all', 'z-30 max-w-280 font-400 typo-caption1', contentMotionClassName, isOutlined ? 'rounded-small border border-Gray-300 bg-Gray-white/[0.98] px-16 py-12 text-Gray-700 shadow-lg backdrop-blur-[5px]' : cn('rounded-xsmall', isDialogMode ? 'p-20' : 'p-12', isLightTheme ? 'bg-Gray-white text-Gray-900 shadow-lg' : 'bg-Gray-black/70 text-Gray-white', isDialogMode && isLightTheme && 'shadow-xl'), className)} {...contentProps} sideOffset={floatingPadding} collisionPadding={floatingPadding} side={side} align={align} alignOffset={-10} onClick={onContentClick} onPointerDownOutside={isDialogMode ? handleClose : undefined} onEscapeKeyDown={isDialogMode ? handleClose : undefined}>
            {(title || showCloseButton) && <div className="mb-8 flex items-center justify-between">
                <div className="font-700 text-O-550 typo-body4">{title}</div>
                {showCloseButton && <button onClick={handleClose} aria-label="닫기" className="flex size-24 items-center justify-center rounded-pill text-Gray-400 transition-colors hover:bg-Gray-100 hover:text-Gray-600">
                    <SvgDelete className="size-16" />
                  </button>}
              </div>}
            <div>{label}</div>
            {isArrow && <RadixTooltip.Arrow className={cn(isOutlined ? 'fill-Gray-white/[0.98]' : isLightTheme ? 'fill-Gray-white' : 'fill-Gray-black/70')} width={14} height={8} />}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>;
};
interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
}
export const TooltipProvider = t0 => {
  const $ = _c(3);
  const {
    children,
    delayDuration: t1
  } = t0;
  const delayDuration = t1 === undefined ? 200 : t1;
  let t2;
  if ($[0] !== children || $[1] !== delayDuration) {
    t2 = <RadixTooltip.Provider delayDuration={delayDuration}>{children}</RadixTooltip.Provider>;
    $[0] = children;
    $[1] = delayDuration;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};